import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, Calendar, Settings, LogOut, BookOpen, CheckCircle2, 
  XCircle, Clock, Eye, Trash2, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        onLogin();
        toast.success('Login successful');
      }
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-12 h-12 bg-[#1E3A8A] rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">Admin Login</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                data-testid="admin-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full btn-primary" 
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Registrations Tab
const RegistrationsTab = ({ registrations, onRefresh }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/registrations/${deleteDialog.id}`);
      toast.success('Registration deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete registration');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      'completed': 'status-badge completed',
      'pending': 'status-badge pending',
      'approved': 'status-badge approved',
      'pending_approval': 'status-badge pending',
      'cancelled': 'status-badge cancelled'
    };
    return classes[status] || 'status-badge pending';
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Student Registrations</h2>
        <Button variant="outline" onClick={onRefresh} data-testid="refresh-registrations-btn">
          Refresh
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th></th>
                <th>Name</th>
                <th>Email</th>
                <th>Test</th>
                <th>Payment</th>
                <th>Booking</th>
                <th>Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {registrations.map((reg) => (
                <>
                  <tr key={reg.id}>
                    <td>
                      <button 
                        onClick={() => setExpandedRow(expandedRow === reg.id ? null : reg.id)}
                        className="p-1 hover:bg-slate-100 rounded"
                      >
                        {expandedRow === reg.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="font-medium">{reg.first_name} {reg.last_name}</td>
                    <td>{reg.email}</td>
                    <td>{reg.test_name}</td>
                    <td><span className={getStatusBadge(reg.payment_status)}>{reg.payment_status}</span></td>
                    <td><span className={getStatusBadge(reg.booking_status)}>{reg.booking_status}</span></td>
                    <td className="font-mono">C${reg.total_amount?.toFixed(2)}</td>
                    <td>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setDeleteDialog({ open: true, id: reg.id })}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`delete-reg-${reg.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                  {expandedRow === reg.id && (
                    <tr>
                      <td colSpan="8" className="bg-slate-50 p-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div><strong>Phone:</strong> {reg.phone}</div>
                          <div><strong>DOB:</strong> {reg.date_of_birth}</div>
                          <div><strong>Gender:</strong> {reg.gender}</div>
                          <div><strong>Nationality:</strong> {reg.nationality}</div>
                          <div><strong>Citizenship:</strong> {reg.country_of_citizenship}</div>
                          <div><strong>Language:</strong> {reg.native_language}</div>
                          <div><strong>Agency:</strong> {reg.agency_referred}</div>
                          <div><strong>Address:</strong> {reg.address}, {reg.city}, {reg.state}</div>
                          {reg.booking_date && (
                            <div><strong>Booking:</strong> {reg.booking_date} at {reg.booking_time}</div>
                          )}
                          <div><strong>Packages:</strong> {reg.selected_packages?.join(', ')}</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
              {registrations.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-8 text-slate-500">
                    No registrations yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this registration? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, id: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Bookings Tab
const BookingsTab = ({ bookings, onRefresh }) => {
  const handleStatusUpdate = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`, { status });
      toast.success(`Booking ${status}`);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Booking Requests</h2>
        <Button variant="outline" onClick={onRefresh} data-testid="refresh-bookings-btn">
          Refresh
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Registration ID</th>
                <th>Date</th>
                <th>Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking) => (
                <tr key={booking.id}>
                  <td className="font-mono text-xs">{booking.registration_id}</td>
                  <td>{booking.booking_date}</td>
                  <td>{booking.booking_time}</td>
                  <td>
                    <span className={`status-badge ${booking.status === 'approved' ? 'approved' : booking.status === 'cancelled' ? 'cancelled' : 'pending'}`}>
                      {booking.status}
                    </span>
                  </td>
                  <td>
                    {booking.status === 'pending_approval' && (
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => handleStatusUpdate(booking.id, 'approved')}
                          data-testid={`approve-${booking.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleStatusUpdate(booking.id, 'cancelled')}
                          data-testid={`cancel-${booking.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Cancel
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-slate-500">
                    No bookings yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Calendar Availability Tab
const CalendarTab = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [isAvailable, setIsAvailable] = useState(true);
  const [loading, setLoading] = useState(false);

  const defaultWeekendSlots = Array.from({ length: 13 }, (_, i) => `${(8 + i).toString().padStart(2, '0')}:00`);
  const defaultWeekdaySlots = ['14:00', '15:00', '16:00', '20:00', '21:00'];

  useEffect(() => {
    fetchAvailability();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const existing = availability.find(a => a.date === dateStr);
      
      if (existing) {
        setTimeSlots(existing.time_slots || []);
        setIsAvailable(existing.is_available !== false);
      } else {
        const day = selectedDate.getDay();
        const isWeekend = day === 0 || day === 6;
        setTimeSlots(isWeekend ? defaultWeekendSlots : defaultWeekdaySlots);
        setIsAvailable(true);
      }
    }
  }, [selectedDate, availability]);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/calendar-availability`);
      setAvailability(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSave = async () => {
    if (!selectedDate) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/calendar-availability`, {
        date: format(selectedDate, 'yyyy-MM-dd'),
        time_slots: timeSlots,
        is_available: isAvailable
      });
      toast.success('Availability updated');
      fetchAvailability();
    } catch (error) {
      toast.error('Failed to update availability');
    } finally {
      setLoading(false);
    }
  };

  const toggleSlot = (slot) => {
    setTimeSlots(prev => 
      prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot].sort()
    );
  };

  const allSlots = [...defaultWeekendSlots];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Calendar Availability</h2>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Select Date</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-md border"
                data-testid="admin-calendar"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {selectedDate ? `Settings for ${format(selectedDate, 'MMMM d, yyyy')}` : 'Select a date'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDate ? (
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <Checkbox 
                    id="available"
                    checked={isAvailable}
                    onCheckedChange={setIsAvailable}
                    data-testid="available-checkbox"
                  />
                  <label htmlFor="available" className="font-medium">
                    Available for bookings
                  </label>
                </div>

                {isAvailable && (
                  <div>
                    <p className="font-medium mb-3">Available Time Slots:</p>
                    <div className="grid grid-cols-4 gap-2">
                      {allSlots.map((slot) => (
                        <button
                          key={slot}
                          onClick={() => toggleSlot(slot)}
                          className={`p-2 text-sm rounded border transition-colors ${
                            timeSlots.includes(slot) 
                              ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]' 
                              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid={`slot-toggle-${slot}`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <Button 
                  className="w-full btn-primary" 
                  onClick={handleSave}
                  disabled={loading}
                  data-testid="save-availability-btn"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">
                Select a date to manage availability
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main Admin Page
export default function AdminPage() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loggedIn = sessionStorage.getItem('adminLoggedIn');
    if (loggedIn === 'true') {
      setIsLoggedIn(true);
      fetchData();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [regRes, bookRes] = await Promise.all([
        axios.get(`${API}/registrations`),
        axios.get(`${API}/bookings`)
      ]);
      setRegistrations(regRes.data);
      setBookings(bookRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    setIsLoggedIn(true);
    sessionStorage.setItem('adminLoggedIn', 'true');
    fetchData();
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    sessionStorage.removeItem('adminLoggedIn');
    navigate('/');
  };

  if (!isLoggedIn) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#1E3A8A] text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Admin Dashboard</h1>
              <p className="text-sm text-white/70">EnglishTestSolution.com</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/10"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-8 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#1E3A8A]" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{registrations.length}</p>
                  <p className="text-sm text-slate-500">Total Registrations</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {bookings.filter(b => b.status === 'pending_approval').length}
                  </p>
                  <p className="text-sm text-slate-500">Pending Approvals</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {registrations.filter(r => r.payment_status === 'completed').length}
                  </p>
                  <p className="text-sm text-slate-500">Paid Registrations</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="registrations" className="space-y-6">
            <TabsList className="bg-white border">
              <TabsTrigger value="registrations" className="data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-2" /> Registrations
              </TabsTrigger>
              <TabsTrigger value="bookings" className="data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white">
                <Calendar className="w-4 h-4 mr-2" /> Bookings
              </TabsTrigger>
              <TabsTrigger value="calendar" className="data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white">
                <Settings className="w-4 h-4 mr-2" /> Calendar
              </TabsTrigger>
            </TabsList>

            <TabsContent value="registrations">
              <RegistrationsTab registrations={registrations} onRefresh={fetchData} />
            </TabsContent>
            
            <TabsContent value="bookings">
              <BookingsTab bookings={bookings} onRefresh={fetchData} />
            </TabsContent>
            
            <TabsContent value="calendar">
              <CalendarTab />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
